import { Module, ActionTree, MutationTree, GetterTree } from "vuex"
import { RootState } from "@/store/RootStore"
import { MaintenanceTicket, MaintenanceTicketStatus, MaintenanceTicketAction, Utils, PaginationInfo } from "rp-components"
import { make } from "vuex-pathify"
import { StoreUtils } from "@/utils/StoreUtils"
import Services from "@/services/Services"
import { WorkflowDocument } from "rp-components"

const namespaced: boolean = true

//===================================
// State
//===================================
export interface MaintenanceActionPayload {
    action: MaintenanceTicketAction
    tickets?: MaintenanceTicket[]
    ticket?: MaintenanceTicket
    parentTicket?: MaintenanceTicket
}

export interface MaintenanceTicketListFilters {
    phrase?: string
    createdAfterFilter?: Date
    createdBeforeFilter?: Date
    nextRecurrenceAfterFilter?: Date
    nextRecurrenceBeforeFilter?: Date
    statusFilterList: string[]
    frequencyFilterList: string[]
    ticketTypeFilterList: string[]
    assigneeFilterList: string[]
    tenantFilterList: string[]
    sortAscending: boolean
    showArchived: boolean
}

export interface MaintenanceTicketsQuery {
    propertyId: number
    statuses: string[]
}

export interface MaintenanceState {
    ticket?: MaintenanceTicket
    originalTicket?: MaintenanceTicket
    tickets?: MaintenanceTicket[]
    statuses?: MaintenanceTicketStatus[]
    notificationOptions?: string[]
    ticketTypes?: string[]
    ticketsPagination: PaginationInfo
    ticketsQuery?: MaintenanceTicketsQuery
    relatedOpenWorkflows?: WorkflowDocument[]
}

export const state: MaintenanceState = {
    ticket: undefined,
    originalTicket: undefined,
    tickets: undefined,
    statuses: undefined,
    notificationOptions: undefined,
    ticketTypes: undefined,
    ticketsPagination: {
        pageSize: 10,
        nextLink: undefined
    },
    ticketsQuery: undefined,
    relatedOpenWorkflows: []
}

//===================================
// Getters
//===================================
export const getters: GetterTree<MaintenanceState, RootState> = {
    getVisibleStatuses: (state) => (isTerminal: boolean) => {
        return state.statuses
            ?.filter((status: any) => {
                return status.isTerminal === isTerminal
            })
            .map((status: any) => {
                return status.key
            })
    },

    ...make.getters(state)
}

//===================================
// Actions
//===================================
export const actions: ActionTree<MaintenanceState, RootState> = {
    ...make.actions(state),

    resetState({ commit }) {
        commit("resetState")
    },

    async getTickets({ dispatch }, payload: { propertyId: number; statuses: string[] }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const pageSize = state.ticketsPagination.pageSize
            const ticketsPage = await Services.maintenance.listTickets(payload.propertyId, payload.statuses, pageSize)
            dispatch("setTickets", ticketsPage?.items?.length > 0 ? ticketsPage.items : undefined)
            dispatch("setTicketsPagination", {
                pageSize: pageSize,
                nextLink: ticketsPage.next,
                total: ticketsPage.total
            })
            dispatch("setTicketsQuery", { propertyId: payload.propertyId, statuses: payload.statuses })
        })
    },

    async loadTicketsPage({ dispatch }, targetPage: number) {
        await StoreUtils.withLoading(dispatch, async () => {
            const query = state.ticketsQuery
            if (!query) {
                return
            }
            const pageSize = state.ticketsPagination.pageSize
            const offset = (targetPage - 1) * pageSize
            const ticketsPage = await Services.maintenance.listTickets(query.propertyId, query.statuses, pageSize, offset)
            dispatch("setTickets", ticketsPage.items)
            dispatch("setTicketsPagination", {
                pageSize: pageSize,
                nextLink: ticketsPage.next,
                total: ticketsPage.total ?? state.ticketsPagination.total
            })
        })
    },

    async loadTicket({ dispatch }, payload: { propertyId: number; ticketId: string }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const ticket = await Services.maintenance.getTicket(payload.propertyId, payload.ticketId)
            dispatch("setTicket", ticket)
        })
    },

    async loadTicketStatuses({ dispatch }, propertyId: number) {
        if (!state.statuses) {
            await StoreUtils.withLoading(dispatch, async () => {
                const statuses = await Services.maintenance.listStatuses(propertyId)
                dispatch("setStatuses", statuses)
            })
        }
    },

    async loadNotificationOptions({ dispatch }, propertyId: number) {
        if (!state.notificationOptions) {
            await StoreUtils.withLoading(dispatch, async () => {
                const options = await Services.maintenance.listNotificationOptions(propertyId)
                dispatch("setNotificationOptions", options)
            })
        }
    },

    async loadTicketTypes({ dispatch }, propertyId: number) {
        if (!state.ticketTypes) {
            await StoreUtils.withLoading(dispatch, async () => {
                const types = await Services.maintenance.listTicketTypes(propertyId)
                dispatch("setTicketTypes", types)
            })
        }
    },

    async saveTicket({ dispatch }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const ticket = await Services.maintenance.saveTicket(state.ticket!)
            dispatch("setTicket", ticket)
            dispatch("setAlert", "Ticket saved", { root: true })
        })
    },

    async createComment({ dispatch }, payload: { ticket: MaintenanceTicket; comment: MaintenanceTicketAction }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const ticket = Utils.clone(payload.ticket)!
            const comment = Utils.clone(payload.comment)!
            comment.ticketId = ticket.ticketId!
            const newComment = await Services.maintenance.createComment(ticket.propertyId, comment)
            ticket.actions.unshift(newComment)
            dispatch("setTicket", ticket)
        })
    },

    async showOpenTickets({ dispatch }, showOpenTickets: boolean) {
        dispatch("setShowingOpenTickets", showOpenTickets)
    },

    async createTicket({ dispatch }, payload: { propertyId: number; name: string; email: string; tenant: string; tenantId: string }) {
        const ticket: MaintenanceTicket = {
            propertyId: payload.propertyId,
            archived: false,
            ticketType: "",
            title: "",
            description: "",
            assigneeName: "UNASSIGNED",
            assigneeUserId: "UNASSIGNED",
            status: "OPEN",
            contactName: payload.name,
            contactPhone: "",
            contactEmail: payload.email,
            recurrenceType: "ONE_TIME",
            actions: [],
            tenantNotificationSetting: "BOTH",
            tenantId: payload.tenantId,
            tenantName: payload.tenant,
            files: [],
            ticketTime: "12:00:00",
            billable: false,
            billableAmount: 0,
            totalMinutesLogged: 0
        }
        dispatch("setTicket", ticket)
        dispatch("setOriginalTicket", Utils.clone(ticket))
    },

    async performAction({ dispatch }, payload: MaintenanceActionPayload) {
        await StoreUtils.withLoading(dispatch, async () => {
            for (const ticket of payload.tickets!) {
                const action = Utils.clone(payload.action)!
                action.ticketId = ticket.ticketId!
                await Services.maintenance.performAction(ticket.propertyId, action)
            }
        })
    },

    async loadRelatedOpenWorkflow({ dispatch }, payload: { propertyId: string; ticketId: string }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const workflows = await Services.maintenance.getOpenWorkflowsForTicket(payload.propertyId, payload.ticketId)
            dispatch("setRelatedOpenWorkflows", workflows)
        })
    }
}

//===================================
// Mutators
//===================================
const mutations: MutationTree<MaintenanceState> = {
    ...make.mutations(state),

    resetState: (state) => {
        state.ticket = undefined
        state.tickets = undefined
        state.statuses = undefined
        state.notificationOptions = undefined
        state.ticketsPagination = {
            pageSize: 10,
            nextLink: undefined
        }
        state.ticketsQuery = undefined
    }
}

//===================================
// Module
//===================================
export const maintenance: Module<MaintenanceState, RootState> = {
    namespaced,
    state: state,
    getters: getters,
    actions: actions,
    mutations: mutations
}
